import type {PluginToggles} from '@forecast-it/design-system';

/**
 * Plate editor plugin toggles shared by the three task-modal editors: the task
 * description, the new-comment composer, and an existing comment being edited.
 *
 * `link: true` exposes the toolbar's link button, which is the only route to
 * attaching a URL to selected text. `paragraphStyles` gates the headings and
 * blockquote dropdown and `todoList` gates the action-item button; both stay off.
 *
 * `image`, `file`, `video` and `audio` each register one inline-media plugin: its
 * element, its toolbar affordance, and the deserializer that turns the matching
 * incoming HTML into a node. They do nothing on their own — every insert path also
 * needs an `imageAdapter`, which is what supplies storage and is passed per
 * surface. Turning one on without an adapter leaves the editor unable to upload or
 * resolve anything, so the button hides itself and pastes fall through to the host.
 *
 * `file`, `video` and `audio` additionally need that adapter's `classify` to return
 * the matching kind. The plugin being registered only means the editor can hold
 * such a node; it is `classify` that decides one ever gets created. All four are on
 * here because the task modal is the surface the inline-media work is for.
 *
 * Annotated with the design system's own `PluginToggles`, which only became possible
 * once the release carrying `file`, `video` and `audio` was pinned. Before that the
 * three keys were excess properties that type-checked purely because every consumer of
 * this object is a `.js` file, so nothing ever compared it against the real type. The
 * annotation is what makes a future drift between these toggles and the design system a
 * compile error rather than a silently ignored key.
 *
 * Keep this a module-level constant and pass it by reference. `RichTextEditor`
 * memoizes its plugin list on this object's identity, and plate-core reacts to a
 * new plugin-list identity by reconfiguring the mounted editor — rebuilding every
 * plugin and re-deserializing the whole document. An inline object literal gets a
 * fresh identity on every render, so that work would repeat on each render of the
 * section, once per editor (a task with many comments mounts one per comment).
 */
export const TASK_MODAL_PLUGIN_TOGGLES: PluginToggles = {
	link: true,
	paragraphStyles: false,
	todoList: false,
	image: true,
	file: true,
	video: true,
	audio: true,
};
